import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminNavbar from '../../components/AdminNavbar';
import axios from '../../api/axios';
import { motion } from 'framer-motion';
import { Utensils, ShoppingBag, DollarSign, Star, TrendingUp, ClipboardList } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';

const AdminDashboard = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [popularItems, setPopularItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchAnalytics();
  }, [admin, navigate]);

  const fetchAnalytics = async () => {
    try {
      const [overviewRes, popularRes] = await Promise.all([
        axios.get('/analytics/overview'),
        axios.get('/analytics/popular-items?limit=5')
      ]);
      setAnalytics(overviewRes.data);
      setPopularItems(popularRes.data);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  if (!admin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-12 flex justify-center">
          <div className="w-16 h-16 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const stats = [
    {
      title: "Total Revenue",
      value: `Rs. ${analytics?.totalRevenue?.toLocaleString() || 0}`,
      subtext: `Today: Rs. ${analytics?.todayRevenue?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: "text-green-500",
      bg: "bg-green-500/10",
      border: "border-green-500/20"
    },
    {
      title: "Total Orders",
      value: analytics?.totalOrders || 0,
      subtext: `Today: ${analytics?.todayOrders || 0}`,
      icon: ClipboardList,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
      border: "border-blue-500/20"
    },
    {
      title: "Pending Orders",
      value: analytics?.pendingOrders || 0,
      subtext: "Needs attention",
      icon: ShoppingBag,
      color: "text-yellow-500",
      bg: "bg-yellow-500/10",
      border: "border-yellow-500/20"
    },
    {
      title: "Average Rating",
      value: `${analytics?.averageRating || '0.0'}`,
      subtext: `${analytics?.totalReviews || 0} reviews`,
      icon: Star,
      color: "text-gold-500",
      bg: "bg-gold-500/10",
      border: "border-gold-500/20"
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-20">
      <AdminNavbar />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="container mx-auto px-6 py-8"
      >
        <div className="flex justify-between items-center mb-10">
          <div>
            <h2 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
              Dashboard Overview
            </h2>
            <p className="text-gray-400 mt-2">Welcome back, Admin</p>
          </div>
          <div className="h-10 px-4 bg-gold-500/10 border border-gold-500/20 rounded-full flex items-center text-gold-400 text-sm">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {stats.map((stat, index) => (
            <motion.div key={index} variants={item}>
              <Card className={`bg-black/40 backdrop-blur-sm border ${stat.border} hover:scale-[1.02] transition-transform duration-300`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className={`text-sm font-medium ${stat.color} mb-1`}>{stat.title}</p>
                      <h3 className="text-3xl font-bold text-white mb-2">{stat.value}</h3>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        {stat.subtext.includes('Today') ? (
                          <TrendingUp className="w-3 h-3 text-green-500" />
                        ) : null}
                        {stat.subtext}
                      </p>
                    </div>
                    <div className={`p-3 rounded-xl ${stat.bg}`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <motion.div variants={item} className="lg:col-span-2 space-y-8">
            <h3 className="text-2xl font-bold text-white mb-6">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: 'Manage Menu', icon: Utensils, link: '/admin/menu', count: `${analytics?.totalMenuItems || 0} items`, color: 'from-purple-500/20 to-blue-500/20' },
                { title: 'View Orders', icon: ShoppingBag, link: '/admin/orders', count: `${analytics?.pendingOrders || 0} pending`, color: 'from-orange-500/20 to-red-500/20' },
                { title: 'Check Reviews', icon: Star, link: '/admin/reviews', count: `${analytics?.totalReviews || 0} reviews`, color: 'from-green-500/20 to-emerald-500/20' }
              ].map((action, idx) => (
                <Link key={idx} to={action.link}>
                  <Card className={`h-full bg-gradient-to-br ${action.color} border-white/5 hover:border-gold-500/50 transition-all hover:-translate-y-1`}>
                    <CardContent className="p-6 flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-full bg-black/40 flex items-center justify-center mb-4 border border-white/10 group-hover:border-gold-500/50">
                        <action.icon className="w-6 h-6 text-gold-400" />
                      </div>
                      <h4 className="text-lg font-bold text-white mb-1">{action.title}</h4>
                      <p className="text-sm text-gray-400">{action.count}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Popular Items */}
          <motion.div variants={item}>
            <h3 className="text-2xl font-bold text-white mb-6">Top Selling</h3>
            <Card className="bg-black/40 backdrop-blur-sm border-gold-500/10">
              <CardContent className="p-0">
                {popularItems.length > 0 ? (
                  <div className="divide-y divide-white/10">
                    {popularItems.map((item, index) => (
                      <div key={index} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold-500/10 text-gold-500 font-bold text-sm">
                            #{index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-white">{item.name}</p>
                            <p className="text-xs text-gray-400">{item.count} orders</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-400">Rs. {item.revenue.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">No sales data yet</div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;

